import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { ContentBadge } from './ContentBadges'
import { Skeleton } from '../Skeleton'
import { CustomizeWithBrandDetails } from './components/CustomizeWithBrandDetails'
import { useSourceContent } from './contexts/useSourceContent'
import { isEmail } from '../../requests/sources'
import { ContentType } from '../../../server/models/GenericContentFields'
import './styles.css'

const CopyParagraph: React.FC<{ text: string }> = ({ text }) => {
  const onClick = () => {
    window.navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  return (
    <button className="copy-paragraph" type="button" onClick={onClick} title="Copy to clipboard">
      { text }
    </button>
  )
}

const CopyTitle: React.FC<{ text: string }> = ({ text }) => {
  const onClick = () => {
    window.navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  return (
    <button className="copy-title" type="button" onClick={onClick} title="Copy to clipboard">
      { text }
    </button>
  )
}

export const ContentCardsContainer: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  return (
    <div className="content-cards-container">
      { children }
    </div>
  )
}

type WrapperProps = {
  children: React.ReactNode
  contentType: ContentType
  contentId: string
}

const ContentCardWrapper: React.FC<WrapperProps> = ({
  contentType,
  contentId,
  children
}) => {
  const [generatingNewContent, setGeneratingNewContent] = useState(false)
  const {
    source,
    onSourceClick,
    createCustomizedContentWithBrandDetails
  } = useSourceContent()

  const handleBrandDetailsSelected = useCallback(async (brandDetailsId: string) => {
    const toastId = toast.loading('Brewing the coffee for this one...')
    setGeneratingNewContent(true)

    await createCustomizedContentWithBrandDetails({
      brandDetailsId,
      contentType,
      contentId
    })

    setGeneratingNewContent(false)
    toast.dismiss(toastId)
  }, [])

  if (!source) {
    return null
  }

  if (generatingNewContent) {
    return <LoadingContentCard />
  }

  return (
    <div className="content-card">
      <button
        className="content-card-header"
        onClick={onSourceClick}
        title="Only see content of this source"
      >
        <div className="icon-container">
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" className="email-icon"> <path d="M16.625 3.5625H2.375C2.21753 3.5625 2.06651 3.62506 1.95516 3.73641C1.84381 3.84776 1.78125 3.99878 1.78125 4.15625V14.25C1.78125 14.5649 1.90636 14.867 2.12906 15.0897C2.35176 15.3124 2.65381 15.4375 2.96875 15.4375H16.0312C16.3462 15.4375 16.6482 15.3124 16.8709 15.0897C17.0936 14.867 17.2188 14.5649 17.2188 14.25V4.15625C17.2188 3.99878 17.1562 3.84776 17.0448 3.73641C16.9335 3.62506 16.7825 3.5625 16.625 3.5625ZM15.0983 4.75L9.5 9.88223L3.90168 4.75H15.0983ZM16.0312 14.25H2.96875V5.50629L9.09848 11.1254C9.20802 11.2259 9.3513 11.2817 9.5 11.2817C9.6487 11.2817 9.79198 11.2259 9.90152 11.1254L16.0312 5.50629V14.25Z"/> </svg>
        </div>
        <div className="email-subject">
          {isEmail(source) ? source.subject : source.title}
        </div>
      </button>

      <div className="content-card-body">
        { children }
      </div>

      <div className="content-card-footer">
        <CustomizeWithBrandDetails
          onBrandDetailsSelected={handleBrandDetailsSelected}
        />
      </div>
    </div>
  )
}

interface SummaryContentProps {
  threeSentenceSummary: string
}

export const ThreeSentenceSummaryCard: React.FC<SummaryContentProps> = ({
  threeSentenceSummary
}) => {
  const { source } = useSourceContent()

  return (
    <ContentCardWrapper contentType="summary" contentId={source._id}>
      <ContentBadge type="summary" />
      <CopyParagraph text={threeSentenceSummary} />
    </ContentCardWrapper>
  )
}

interface BlogOutlineContentProps {
  blog: {
      _id: string
      title: string
      outlinePoints: string[]
  }
}
export const BlogOutlineContentCard: React.FC<BlogOutlineContentProps> = ({
  blog
}) => {
  return (
    <ContentCardWrapper
      contentId={blog._id}
      contentType="blog"
    >
      <ContentBadge type="blog" />

      <CopyTitle text={blog.title} />
      {
        blog.outlinePoints.map((point, index) => (
          <CopyParagraph text={point} key={index} />
        ))
      }
    </ContentCardWrapper>
  )
}

interface VideoScriptContentProps {
  shortVideoScript: {
      _id: string
      hook: string
      script: string
  }
}
export const VideoScriptContentCard: React.FC<VideoScriptContentProps> = ({
  shortVideoScript
}) => {
  return (
    <ContentCardWrapper
      contentId={shortVideoScript._id}
      contentType="shortVideo"
    >
      <ContentBadge type="shortVideo" />

      <CopyTitle text={shortVideoScript.hook} />
      <CopyParagraph text={shortVideoScript.script} />
    </ContentCardWrapper>
  )
}

interface CarouselSlideContentProps {
  slides: {
      _id: string
      title: string
      outlinePoints: string[]
  }
}
export const CarouselSlidesContentCard: React.FC<CarouselSlideContentProps> = ({
  slides,
}) => {
  return (
    <ContentCardWrapper
      contentId={slides._id}
      contentType="carousel"
    >
      <ContentBadge type="carousel" />

      <CopyTitle text={slides.title} />
      {
        slides.outlinePoints.map((point, index) => (
          <CopyParagraph text={point} key={index} />
        ))
      }
    </ContentCardWrapper>
  )
}

export const LoadingContentCard: React.FC = () => {
  return (
    <div className="content-card">
      <button
        className="content-card-header"
        title="Only see content of this email"
      >
        <div className="icon-container">
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" className="email-icon"> <path d="M16.625 3.5625H2.375C2.21753 3.5625 2.06651 3.62506 1.95516 3.73641C1.84381 3.84776 1.78125 3.99878 1.78125 4.15625V14.25C1.78125 14.5649 1.90636 14.867 2.12906 15.0897C2.35176 15.3124 2.65381 15.4375 2.96875 15.4375H16.0312C16.3462 15.4375 16.6482 15.3124 16.8709 15.0897C17.0936 14.867 17.2188 14.5649 17.2188 14.25V4.15625C17.2188 3.99878 17.1562 3.84776 17.0448 3.73641C16.9335 3.62506 16.7825 3.5625 16.625 3.5625ZM15.0983 4.75L9.5 9.88223L3.90168 4.75H15.0983ZM16.0312 14.25H2.96875V5.50629L9.09848 11.1254C9.20802 11.2259 9.3513 11.2817 9.5 11.2817C9.6487 11.2817 9.79198 11.2259 9.90152 11.1254L16.0312 5.50629V14.25Z"/> </svg>
        </div>
        <div className="email-subject">
          <Skeleton width="280px" />
        </div>
      </button>
      <div className="content-card-body">
        <Skeleton width="120px" />
        <br />
        <br />
        <Skeleton />
        <br />
        <br />
        <Skeleton width="90%" />
        <br />
        <Skeleton width="70%" />
        <br />
        <Skeleton width="75%" />
        <br />
        <br />
        <Skeleton />
        <br />
        <br />
        <Skeleton width="90%" />
        <br />
        <Skeleton width="70%" />
        <br />
        <Skeleton width="75%" />
        <br />
        <br />
      </div>
      <div className="content-card-footer">
        <Skeleton width="130px" />
      </div>
    </div>
  )
}
